import React, { useState, useMemo, useRef, useEffect, useCallback } from "react"
import Tooltip from "aldoo-ra/Tooltip"
import TextBundle from "aldoo-ra/CMS/text-bundle"

// Wrap a name span with a tooltip. Hover shows on desktop; tap toggles on
// mobile (auto-dismisses). Native title removed since Tooltip replaces it.
function NameWithTip({ name, className, disableTap }) {
  const ref = useRef(null)
  const [tapOpen, setTapOpen] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const checkTruncation = () => {
    const el = ref.current
    if (!el) return
    setTruncated(el.scrollWidth > el.clientWidth + 1)
  }
  useEffect(() => { checkTruncation() }, [name])
  useEffect(() => {
    if (!tapOpen) return
    const t = setTimeout(() => setTapOpen(false), 2500)
    return () => clearTimeout(t)
  }, [tapOpen])
  const span = (
    <span
      ref={ref}
      className={className}
      onClick={(e) => { if (!disableTap && truncated) { e.stopPropagation(); setTapOpen((v) => !v) } }}
      onMouseEnter={checkTruncation}
    >
      {name}
    </span>
  )
  if (!truncated) return span
  return (
    <Tooltip text={name} position="top" display="block" {...(tapOpen ? { isOpen: true } : {})}>
      {span}
    </Tooltip>
  )
}
import {
  ChevronRightIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline"
import {
  FolderIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/solid"
import { FileIcon, defaultStyles } from "react-file-icon"
import { GetShareThumb, GetSharePreviewUrl } from "./api"
import FilePreview from "./FilePreview"

function fileExt(name) {
  const i = name.lastIndexOf(".")
  return i > 0 ? name.slice(i + 1).toLowerCase() : ""
}

function FileTypeIcon({ name, className = "w-4" }) {
  const ext = fileExt(name)
  return (
    <span className={`${className} shrink-0 block`}>
      <FileIcon extension={ext} {...(defaultStyles[ext] || {})} />
    </span>
  )
}

// Extensions the server will thumbnail (mirrors API THUMB_IMAGE_EXTS). The
// generated thumb is always JPEG, so even heic/tiff sources render in <img>.
const THUMB_EXTS = new Set(["jpg", "jpeg", "png", "heic", "heif", "gif", "webp", "bmp", "tiff", "tif"])

// Image formats browsers render natively from the original file.
const IMAGE_RENDER_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"])
// Image formats browsers can't render directly — preview the JPEG thumbnail.
const IMAGE_THUMB_ONLY_EXTS = new Set(["heic", "heif", "tiff", "tif"])
const VIDEO_EXTS = new Set(["mp4", "webm", "ogg", "ogv", "m4v", "mov"])

// What kind of inline preview a file supports, and whether the image must come
// from the JPEG thumbnail (heic/tiff) rather than the original bytes.
function previewKind(name) {
  const ext = fileExt(name)
  if (ext === "pdf") return { kind: "pdf", useThumb: false }
  if (VIDEO_EXTS.has(ext)) return { kind: "video", useThumb: false }
  if (IMAGE_RENDER_EXTS.has(ext)) return { kind: "image", useThumb: false }
  if (IMAGE_THUMB_ONLY_EXTS.has(ext)) return { kind: "image", useThumb: true }
  return null
}

// Cache thumb-URL promises per token:index so reopening the tree / re-renders
// don't refetch. Misses are dropped so a failed lookup can retry on next mount.
const thumbCache = new Map()
function loadThumb(token, fileIndex, password) {
  const key = `${token}:${fileIndex}`
  if (thumbCache.has(key)) return thumbCache.get(key)
  const p = GetShareThumb(token, fileIndex, password)
    .then((r) => r?.url || null)
    .catch(() => null)
  p.then((url) => { if (!url) thumbCache.delete(key) })
  thumbCache.set(key, p)
  return p
}

// Server thumbnail tile (view surfaces with a share token). Lazy — only fetches
// once the row scrolls near the viewport. Non-images / failures fall back to the
// file-type icon inside the tile. Callers without a token render FileTypeIcon
// directly (keeps hooks unconditional here).
function FileThumb({ name, token, fileIndex, password }) {
  const ref = useRef(null)
  const [url, setUrl] = useState(null)
  const [seen, setSeen] = useState(false)
  const eligible = fileIndex != null && THUMB_EXTS.has(fileExt(name))

  useEffect(() => {
    if (!eligible || seen) return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect() }
    }, { rootMargin: "200px" })
    io.observe(el)
    return () => io.disconnect()
  }, [eligible, seen])

  useEffect(() => {
    if (!eligible || !seen) return
    let alive = true
    loadThumb(token, fileIndex, password).then((u) => { if (alive) setUrl(u) })
    return () => { alive = false }
  }, [eligible, seen, token, fileIndex, password])

  if (url) {
    return (
      <img
        ref={ref}
        src={url}
        alt={name}
        loading="lazy"
        className="w-14 h-14 shrink-0 rounded-lg object-cover bg-neutral_1"
      />
    )
  }
  return (
    <span ref={ref} className="w-14 h-14 shrink-0 rounded-lg bg-neutral_1 flex items-center justify-center">
      <FileTypeIcon name={name} className="w-8" />
    </span>
  )
}

function formatBytes(bytes) {
  if (!bytes) return "0 B"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2))} GB`
}

// Build a nested tree from flat entries: { path, file?, size?, index? }.
function buildTree(entries) {
  const root = { name: "", path: "", isFolder: true, children: new Map(), size: 0 }
  for (const entry of entries) {
    const { path } = entry
    const size = entry.size != null ? entry.size : (entry.file?.size || 0)
    const parts = path.split("/").filter(Boolean)
    let node = root
    let acc = ""
    parts.forEach((part, i) => {
      acc = acc ? `${acc}/${part}` : part
      const isLeaf = i === parts.length - 1
      if (isLeaf) {
        node.children.set(part, { name: part, path: acc, isFolder: false, size, index: entry.index, children: new Map() })
      } else {
        if (!node.children.has(part)) {
          node.children.set(part, { name: part, path: acc, isFolder: true, children: new Map(), size: 0 })
        }
        node = node.children.get(part)
      }
    })
  }
  // Roll up folder sizes
  const sumSize = (n) => {
    if (!n.isFolder) return n.size
    let total = 0
    for (const c of n.children.values()) total += sumSize(c)
    n.size = total
    return total
  }
  sumSize(root)
  return root
}

// Folders first, then files, each alphabetical.
function sortedChildren(node) {
  return [...node.children.values()].sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}

// Walk a folder subtree to determine whether any descendant is in `missing`
// status (used to show the per-folder "Find" button during resume).
function hasMissingDescendant(node, statusMap) {
  if (!node.isFolder) return statusMap?.[node.path] === 'missing'
  for (const c of node.children.values()) {
    if (hasMissingDescendant(c, statusMap)) return true
  }
  return false
}

function TreeNode({ node, depth, onRemove, onDownload, onDownloadFolder, onFindFolder, disabled, progressMap, statusMap, rowRefs, texts, shareToken, password, onOpenPreview }) {
  const [open, setOpen] = useState(depth < 1)
  const pad = { paddingLeft: `${depth * 16 + 12}px` }

  if (!node.isFolder) {
    const progress = progressMap?.[node.path]
    const status = statusMap?.[node.path]
    const isUploading = status === 'uploading'
    const isDone = status === 'done'
    const isError = status === 'error'
    const isMissing = status === 'missing'
    return (
      <div
        ref={(el) => { if (rowRefs) rowRefs.current[node.path] = el }}
        className={`flex flex-col gap-1 py-2 pr-3 hover:bg-black/5 ${isMissing ? 'opacity-50' : ''}`}
        style={pad}
      >
        <div className={`flex items-center ${shareToken ? "gap-3" : "gap-2"}`}>
          {(() => {
            const canPreview = onOpenPreview && shareToken && node.index != null && previewKind(node.name)
            if (canPreview) {
              return (
                <button
                  type="button"
                  onClick={() => onOpenPreview(node)}
                  className="flex items-center gap-3 min-w-0 flex-1 text-left group"
                  title={texts?.preview || "Preview"}
                >
                  <FileThumb name={node.name} token={shareToken} fileIndex={node.index} password={password} />
                  <span className="flex-1 min-w-0 block">
                    <NameWithTip name={node.name} className="block text-sm text-text truncate group-hover:underline" disableTap />
                  </span>
                </button>
              )
            }
            return (
              <>
                {shareToken
                  ? <FileThumb name={node.name} token={shareToken} fileIndex={node.index} password={password} />
                  : <FileTypeIcon name={node.name} />}
                <div className="flex-1 min-w-0">
                  <NameWithTip name={node.name} className="block text-sm text-text truncate" />
                </div>
              </>
            )
          })()}
          <span className="text-xs text-text_secondary shrink-0">{formatBytes(node.size)}</span>
          {isMissing && (
            <span className="text-[10px] uppercase tracking-wide text-text_secondary border border-dashed border-bordered rounded px-1.5 py-0.5 shrink-0">
              {texts?.missing}
            </span>
          )}
          {isDone && <span className="text-xs text-success shrink-0">✓</span>}
          {isError && <span className="text-xs text-error shrink-0">!</span>}
          {onDownload && (
            <button
              type="button"
              onClick={() => onDownload(node.index)}
              disabled={disabled}
              className="p-1 rounded text-text hover:bg-neutral_1 transition-colors disabled:opacity-40"
              title={texts?.download}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(node.path, false)}
              disabled={disabled}
              className="p-1 rounded text-text_secondary hover:text-error hover:bg-neutral_1 transition-colors disabled:opacity-40"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {isUploading && (
          <div className="h-1.5 rounded-full bg-neutral_1 overflow-hidden">
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${Math.round((progress || 0) * 100)}%` }}
            />
          </div>
        )}
      </div>
    )
  }

  const children = sortedChildren(node)
  const folderHasMissing = onFindFolder && hasMissingDescendant(node, statusMap)
  return (
    <div className="divide-y divide-neutral_1">
      <div className="flex items-center gap-1.5 py-2 pr-3 hover:bg-black/5" style={pad}>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
          <ChevronRightIcon className={`h-4 w-4 text-text_secondary shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          {open ? <FolderOpenIcon className="h-4 w-4 text-primary shrink-0" /> : <FolderIcon className="h-4 w-4 text-primary shrink-0" />}
          <div className="flex-1 min-w-0">
            <NameWithTip name={node.name} className="block text-sm font-medium text-text truncate" />
          </div>
        </button>
        <span className="text-xs text-text_secondary shrink-0">{formatBytes(node.size)}</span>
        {onDownloadFolder && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDownloadFolder(node.path) }}
            disabled={disabled}
            className="p-1 rounded text-text hover:bg-neutral_1 transition-colors disabled:opacity-40"
            title={texts?.downloadFolderAsZip}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
        )}
        {folderHasMissing && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFindFolder(node.path) }}
            disabled={disabled}
            className="text-[11px] font-semibold uppercase tracking-wide px-3 py-1 min-w-[64px] rounded-full border border-primary text-primary hover:bg-primary hover:text-white transition-colors disabled:opacity-40"
            title={texts?.findFolder}
          >
            {texts?.find}
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(node.path, true)}
            disabled={disabled}
            className="p-1 rounded text-text_secondary hover:text-error hover:bg-neutral_1 transition-colors disabled:opacity-40"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && children.map((c) => (
        <TreeNode key={c.path} node={c} depth={depth + 1} onRemove={onRemove} onDownload={onDownload} onDownloadFolder={onDownloadFolder} onFindFolder={onFindFolder} disabled={disabled} progressMap={progressMap} statusMap={statusMap} rowRefs={rowRefs} texts={texts} shareToken={shareToken} password={password} onOpenPreview={onOpenPreview} />
      ))}
    </div>
  )
}

export default function FileTree({ entries, onRemove, onDownload, onDownloadFolder, onFindFolder, disabled, progressMap, statusMap, activePath, maxHeightClass = "max-h-[50vh]", shareToken, password }) {
  const texts = TextBundle({ id: "transfer_share" })
  const root = useMemo(() => buildTree(entries), [entries])
  const children = sortedChildren(root)
  const scrollRef = useRef(null)
  const rowRefs = useRef({})
  // True while we're driving the scroll ourselves — flips off briefly so the
  // user-scroll detector doesn't fire on our own scrollIntoView call.
  const programmaticScrollRef = useRef(false)
  // Once the user scrolls manually, stop auto-following. Reset to allow
  // again only when the active item changes to one outside the viewport
  // entirely (i.e. the user is back to following the upload).
  const [userScrolled, setUserScrolled] = useState(false)

  // Reset user-scroll suspension whenever the active item changes — gives
  // them auto-follow again on the next file.
  useEffect(() => { setUserScrolled(false) }, [activePath])

  // ── Inline preview overlay ──────────────────────────────────────────────────
  // Only wired when a share token is present (view surfaces). Clicking an
  // image/video row fetches a signed URL (preview mode — no download count) and
  // opens the lightbox. heic/tiff fall back to their JPEG thumbnail.
  const [preview, setPreview] = useState(null) // { kind, name } | null
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(false)

  const openPreview = useCallback(async (node) => {
    if (!shareToken || node.index == null) return
    const info = previewKind(node.name)
    if (!info) return
    setPreview({ kind: info.kind, name: node.name })
    setPreviewUrl(null)
    setPreviewError(false)
    setPreviewLoading(true)
    try {
      const res = info.useThumb
        ? await GetShareThumb(shareToken, node.index, password)
        : await GetSharePreviewUrl(shareToken, node.index, password)
      if (res?.url) setPreviewUrl(res.url)
      else setPreviewError(true)
    } catch {
      setPreviewError(true)
    } finally {
      setPreviewLoading(false)
    }
  }, [shareToken, password])

  const closePreview = useCallback(() => {
    setPreview(null)
    setPreviewUrl(null)
    setPreviewError(false)
  }, [])

  const onOpenPreview = shareToken ? openPreview : undefined

  // When the active item changes (next file starts uploading), park it at
  // the top of the scroll container — but only if it's outside the visible
  // area (don't scroll when the next file was already visible). Triggers
  // only on activePath change, not on every progress tick.
  useEffect(() => {
    if (!activePath || userScrolled) return
    const container = scrollRef.current
    const row = rowRefs.current[activePath]
    if (!container || !row) return
    const cRect = container.getBoundingClientRect()
    const rRect = row.getBoundingClientRect()
    // Only scroll when the row isn't already comfortably in view.
    const fullyVisible = rRect.top >= cRect.top && rRect.bottom <= cRect.bottom
    if (fullyVisible) return
    // Compute the row's top inside the container's CONTENT box (inside the
    // top border). Rect math is in the list's own coordinate system; offsetTop
    // walks the offsetParent chain and was giving the wrong delta for deeply
    // nested rows (folder > sub > file) when folder wrappers introduced
    // their own layout contexts.
    const borderTop = parseFloat(getComputedStyle(container).borderTopWidth) || 0
    const delta = rRect.top - cRect.top - borderTop
    const max = container.scrollHeight - container.clientHeight
    const targetTop = Math.max(0, Math.min(container.scrollTop + delta, max))
    programmaticScrollRef.current = true
    container.scrollTo({ top: targetTop, behavior: 'smooth' })
    setTimeout(() => { programmaticScrollRef.current = false }, 600)
  }, [activePath, userScrolled])

  const handleScroll = () => {
    if (programmaticScrollRef.current) return
    if (!userScrolled) setUserScrolled(true)
  }

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onWheel={handleScroll}
        onTouchMove={handleScroll}
        className={`relative rounded-2xl border border-bordered overflow-hidden divide-y divide-neutral_1 overflow-y-auto ${maxHeightClass}`}
      >
        {children.map((c) => (
          <TreeNode key={c.path} node={c} depth={0} onRemove={onRemove} onDownload={onDownload} onDownloadFolder={onDownloadFolder} onFindFolder={onFindFolder} disabled={disabled} progressMap={progressMap} statusMap={statusMap} rowRefs={rowRefs} texts={texts} shareToken={shareToken} password={password} onOpenPreview={onOpenPreview} />
        ))}
      </div>
      <FilePreview
        open={!!preview}
        kind={preview?.kind}
        url={previewUrl}
        fileName={preview?.name}
        loading={previewLoading}
        error={previewError}
        onClose={closePreview}
      />
    </>
  )
}
